import axios from 'axios';
import axiosRetry from 'axios-retry';
import DOMPurify from 'dompurify';
import { marked } from 'marked';
import { AnimatePresence, domAnimation, LazyMotion } from 'motion/react';
import * as m from 'motion/react-m';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  clearOnboardingPreference,
  clearSessionOnboarding,
  getBrowserStorage,
  readOnboardingPreference,
  readSessionOnboarding,
  saveOnboardingPreference,
  saveSessionOnboarding,
} from '../onboardingStorage';
import AgentPresence from './AgentPresence';
import ActionBeam, { BeamButton } from './ActionBeam';

axiosRetry(axios, {
  retries: 3,
  retryDelay: (retryCount) => retryCount * 1000,
  retryCondition: (error) => error.response?.status === 429,
});

const DEFAULT_LABELS = {
  modalEyebrow: 'Assistente IA',
  modalTitle: 'Orientati nei contenuti INAPP',
  welcomeMessage:
    'Posso aiutarti a comprendere questa pagina e le informazioni disponibili.',
  promptPlaceholder: 'Scrivi la tua domanda',
  resetTitle: 'Nuova conversazione',
  closeTitle: 'Chiudi assistente',
  submitTitle: 'Invia domanda',
  hint: 'Invio per inviare · Shift + Invio per andare a capo',
};

const DEFAULT_MAX_DRAFT_LENGTH = 1200;
const DEFAULT_MAX_HISTORY_ITEMS = 8;
const DEFAULT_MAX_USER_MESSAGES_PER_SESSION = 20;
const DEFAULT_MAX_PAGE_CONTEXT_CHARS = 8000;
const DEFAULT_PAGE_CONTEXT_STRATEGY = 'first_message_only';
const DEFAULT_STRAND_COLORS = ['#00d5ff', '#00aaff', '#07d5b2'];

const getFrontendConfig = () => {
  if (typeof window === 'undefined') {
    return {};
  }

  return window.KapusonsAiChatbot || {};
};

const getLabels = () => {
  const config = getFrontendConfig();

  return {
    ...DEFAULT_LABELS,
    ...(config.labels || {}),
  };
};

const getLimits = () => {
  const config = getFrontendConfig();
  const limits = config.limits || {};

  return {
    maxDraftLength:
      Number.isFinite(Number(limits.maxDraftLength)) &&
        Number(limits.maxDraftLength) > 0
        ? Number(limits.maxDraftLength)
        : DEFAULT_MAX_DRAFT_LENGTH,
    maxHistoryItems:
      Number.isFinite(Number(limits.maxHistoryItems)) &&
        Number(limits.maxHistoryItems) > 0
        ? Number(limits.maxHistoryItems)
        : DEFAULT_MAX_HISTORY_ITEMS,
    maxUserMessagesPerSession:
      Number.isFinite(Number(limits.maxUserMessagesPerSession)) &&
        Number(limits.maxUserMessagesPerSession) > 0
        ? Number(limits.maxUserMessagesPerSession)
        : DEFAULT_MAX_USER_MESSAGES_PER_SESSION,
    maxPageContextChars:
      Number.isFinite(Number(limits.maxPageContextChars)) &&
        Number(limits.maxPageContextChars) > 0
        ? Number(limits.maxPageContextChars)
        : DEFAULT_MAX_PAGE_CONTEXT_CHARS,
  };
};

const getPageContextConfig = () => {
  const config = getFrontendConfig();
  const pageContext = config.pageContext || {};
  const strategy = ['first_message_only', 'always', 'never'].includes(
    pageContext.strategy
  )
    ? pageContext.strategy
    : DEFAULT_PAGE_CONTEXT_STRATEGY;

  return {
    strategy,
    allowed: pageContext.allowed === true,
  };
};

const getExperienceConfig = () => {
  const config = getFrontendConfig();
  const persistence = config.onboardingPersistence || {};

  return {
    privacyUrl:
      typeof config.privacyUrl === 'string' ? config.privacyUrl : '',
    persistenceEnabled: persistence.enabled === true,
    persistenceMaxAgeDays:
      Number.isFinite(Number(persistence.maxAgeDays)) &&
        Number(persistence.maxAgeDays) > 0
        ? Number(persistence.maxAgeDays)
        : 180,
  };
};

const getAppearanceConfig = () => {
  const config = getFrontendConfig();
  const configuredColors = config.appearance?.strandColors;
  const strandColors = Array.isArray(configuredColors)
    ? configuredColors.filter(
        (color) => typeof color === 'string' && /^#[0-9a-f]{6}$/i.test(color)
      )
    : [];

  return {
    strandColors:
      strandColors.length === DEFAULT_STRAND_COLORS.length
        ? strandColors
        : DEFAULT_STRAND_COLORS,
  };
};

const getEndpoint = () => {
  const config = getFrontendConfig();

  return config.endpoint || '/wp-json/kapusons-ai-chatbot/v1/message';
};

const getOpenEventName = () => {
  const config = getFrontendConfig();

  return config.openEvent || 'chatbot:open';
};

const renderMarkdown = (text) =>
  DOMPurify.sanitize(marked.parse(text || '', { async: false }))
    .replace(/<table>/g, '<div class="chatbot-table-scroll" role="region" aria-label="Tabella dati" tabindex="0"><table>')
    .replace(/<\/table>/g, '</table></div>');

const DIALOG_TITLE_ID = 'kapusons-ai-chatbot-dialog-title';
const PROMPT_ID = 'kapusons-ai-chatbot-prompt';
const PROMPT_LABEL_ID = 'kapusons-ai-chatbot-prompt-label';
const PROMPT_DESCRIPTION_ID = 'kapusons-ai-chatbot-prompt-description';
const INTRO_TITLE_ID = 'kapusons-ai-chatbot-intro-title';
const RESET_SCOPE_DESCRIPTION =
  'Rimuove messaggi e bozza soltanto da questa sessione del browser.';
const STORAGE_KEY = 'kapusons_ai_chatbot_session';
const generateSessionId = () =>
  'kaic_session_' + Date.now().toString(36) + '_' + Math.random().toString(36).substring(2, 9);
const EXPERIENCE_FIRST_RUN = 'first-run';
const EXPERIENCE_RETURNING = 'returning-empty';
const EXPERIENCE_CONVERSATION = 'conversation';

const INTRO_BEATS = [
  {
    title: 'Ciao, sono l’assistente IA di PIAAC.',
  },
  {
    title: 'Posso spiegare contenuti e fonti con parole più semplici.',
  },
  {
    title: 'Il tuo compagno di ricerca su PIAAC.',
  },
];
const INTRO_BEAT_DURATION_MS = 4000;
const INTRO_BEAT_TRANSITION_SECONDS = 0.52;
const INTRO_BEAT_DELAYS = [
  INTRO_BEAT_DURATION_MS - INTRO_BEAT_TRANSITION_SECONDS * 1000,
  INTRO_BEAT_DURATION_MS,
];

const INTRO_ACTIONS = [
  {
    label: 'Evidenze e risultati',
    description: 'Dati, report e principali risultati dell’indagine.',
    icon: 'bi-file-earmark-text',
    prompt: 'Quali sono i principali risultati dell’indagine PIAAC per l’Italia e come si posiziona rispetto agli altri Paesi?',
  },
  {
    label: 'Metodologia',
    description: 'Approfondisci strumenti, campioni e procedure.',
    icon: 'bi-pie-chart',
    prompt: 'Come vengono misurate le competenze nell’indagine PIAAC e quali metodologie di ricerca vengono utilizzate?',
  },
  {
    label: 'Concetti e definizioni',
    description: 'Glossario e concetti chiave dell’indagine PIAAC.',
    icon: 'bi-book',
    prompt: 'Quali sono i concetti chiave, le definizioni di competenza e i domini analizzati da PIAAC?',
  },
];

const INTRO_CAPABILITIES = [
  {
    title: 'Risposte contestuali',
    description: 'Uso la domanda e, quando attivo, il contenuto pubblico della pagina.',
    icon: 'bi-compass',
  },
  {
    title: 'Fonti da verificare',
    description: 'Ti aiuto a leggere riferimenti e materiali senza sostituire le verifiche.',
    icon: 'bi-link-45deg',
  },
  {
    title: 'Domande naturali',
    description: 'Scrivi con parole tue e approfondisci un tema alla volta.',
    icon: 'bi-chat-left-text',
  },
];

const TRIGGER_SELECTOR = '.ask-ai, [data-kapusons-ai-chatbot-trigger]';
const BODY_OPEN_CLASS = 'kapusons-ai-chatbot-open';
const PAGE_CONTEXT_SOURCE_SELECTOR = [
  'main article',
  'main',
  '[role="main"]',
  '.entry-content',
  '.wp-block-post-content',
  '.site-main',
  '.content-area',
  'article',
].join(',');
const PAGE_CONTEXT_EXCLUDE_SELECTOR = [
  'script',
  'style',
  'noscript',
  'svg',
  'canvas',
  'iframe',
  'form',
  'input',
  'textarea',
  'select',
  'option',
  '[contenteditable]',
  '[hidden]',
  '[aria-hidden="true"]',
  '[data-private]',
  '[data-sensitive]',
  '.private',
  '.account',
  '.profile',
  'nav',
  'header',
  'footer',
  'aside',
  '.widget',
  '.wp-block-search',
  '.search-form',
  '.comments-area',
  '.comment-respond',
  '.post-navigation',
  '.pagination',
  '.breadcrumb',
  '.screen-reader-text',
  '[role="navigation"]',
  '[role="search"]',
  '[data-kapusons-ai-chatbot-root]',
  '#kapusons-ai-chatbot-root',
  '.kapusons-chatbot',
  '.chatbot-modal',
  '.chatbot-modal__dialog',
  '.ask-ai',
  '[data-kapusons-ai-chatbot-trigger]',
].join(',');
const PAGE_CONTEXT_NOISE_PATTERNS = [
  /^recent posts$/i,
  /^recent comments$/i,
  /^search$/i,
  /^searchsearch$/i,
  /^hello world!?$/i,
  /^welcome to wordpress\.\s*this is your first post\..*$/i,
  /^post author\s*:.*$/i,
  /^post published\s*:.*$/i,
  /^post category\s*:.*$/i,
  /^post comments\s*:.*$/i,
  /^continue reading.*$/i,
  /^a wordpress commenter on .*$/i,
];

const repairCommonMojibake = (text = '') =>
  String(text)
    .replace(/\u00c3\u00a0/g, '\u00e0')
    .replace(/\u00c3\u00a8/g, '\u00e8')
    .replace(/\u00c3\u00a9/g, '\u00e9')
    .replace(/\u00c3\u00ac/g, '\u00ec')
    .replace(/\u00c3\u00b2/g, '\u00f2')
    .replace(/\u00c3\u00b9/g, '\u00f9')
    .replace(/\u00c3\u00b1/g, '\u00f1')
    .replace(/\u00e2\u20ac\u2122/g, "'")
    .replace(/\u00e2\u20ac\u0153/g, '"')
    .replace(/\u00e2\u20ac\u009d/g, '"')
    .replace(/\u00e2\u20ac\u00a6/g, '...');

const normalizeWhitespace = (text) =>
  (text || '')
    .replace(/\u00a0/g, ' ')
    .replace(/[ \t]+/g, ' ')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

const cleanPageContextText = (text) => {
  const raw = (text || '').replace(/\u00a0/g, ' ');
  const lower = raw.toLowerCase();
  const starterHits = [
    'welcome to wordpress. this is your first post',
    'hello world!',
    'recent posts',
    'recent comments',
    'uncategorized',
  ].filter((marker) => lower.includes(marker)).length;

  if (starterHits >= 3) {
    return '';
  }

  const lines = raw
    .split(/\r?\n/u)
    .map((line) => line.trim())
    .filter(Boolean)
    .filter(
      (line) =>
        !PAGE_CONTEXT_NOISE_PATTERNS.some((pattern) => pattern.test(line))
    );

  return normalizeWhitespace(lines.join('\n'));
};

const normalizePromptText = (text) =>
  (text || '')
    .replace(/\u00a0/g, ' ')
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .replace(/\n{4,}/g, '\n\n\n');

const truncatePromptText = (text, maxDraftLength) => {
  if (text.length <= maxDraftLength) {
    return text;
  }

  return text.slice(0, maxDraftLength);
};

const getEditableText = (element) => {
  if (!element) {
    return '';
  }

  return normalizePromptText(element.innerText || '').replace(/\n$/u, '');
};

const setEditableText = (element, text) => {
  if (!element) {
    return;
  }

  element.textContent = text || '';
};

const moveCaretToEnd = (element) => {
  if (!element || typeof window === 'undefined') {
    return;
  }

  const selection = window.getSelection();

  if (!selection) {
    return;
  }

  const range = document.createRange();

  range.selectNodeContents(element);
  range.collapse(false);

  selection.removeAllRanges();
  selection.addRange(range);
};

const insertPlainTextAtSelection = (container, text) => {
  if (!container || typeof window === 'undefined') {
    return;
  }

  const selection = window.getSelection();

  if (!selection || selection.rangeCount === 0) {
    container.textContent = `${container.textContent || ''}${text}`;
    moveCaretToEnd(container);
    return;
  }

  const range = selection.getRangeAt(0);

  if (!container.contains(range.commonAncestorContainer)) {
    container.textContent = `${container.textContent || ''}${text}`;
    moveCaretToEnd(container);
    return;
  }

  range.deleteContents();

  const textNode = document.createTextNode(text);
  range.insertNode(textNode);

  range.setStartAfter(textNode);
  range.setEndAfter(textNode);

  selection.removeAllRanges();
  selection.addRange(range);
};

const getCurrentPageText = () => {
  if (typeof document === 'undefined' || !document.body) {
    return '';
  }

  const source = document.querySelector(PAGE_CONTEXT_SOURCE_SELECTOR);

  if (!source) {
    return '';
  }

  const clone = source.cloneNode(true);

  clone
    .querySelectorAll(PAGE_CONTEXT_EXCLUDE_SELECTOR)
    .forEach((node) => node.remove());

  return cleanPageContextText(clone.textContent || '');
};

const createPageContext = ({ includeText = true, maxChars = DEFAULT_MAX_PAGE_CONTEXT_CHARS } = {}) => {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return {
      url: '',
      title: '',
      text: '',
      captured_at: '',
    };
  }

  const text = includeText
    ? truncatePromptText(getCurrentPageText(), maxChars)
    : '';

  const currentUrl = new URL(window.location.href);

  return {
    url: `${currentUrl.origin}${currentUrl.pathname}`,
    title: document.title || '',
    text,
    captured_at: new Date().toISOString(),
  };
};

const normalizeMessageHistory = (history, maxItems = DEFAULT_MAX_HISTORY_ITEMS) => {
  if (!Array.isArray(history)) {
    return [];
  }

  return history
    .filter((entry) => {
      return (
        entry &&
        typeof entry === 'object' &&
        ['user', 'assistant'].includes(entry.role) &&
        typeof entry.content === 'string' &&
        entry.content.trim() !== ''
      );
    })
    .slice(-maxItems);
};

const normalizeMessages = (messages, labels) => {
  if (!Array.isArray(messages) || messages.length === 0) {
    return [];
  }

  const validMessages = messages.filter((message) => {
    return (
      message &&
      typeof message === 'object' &&
      typeof message.id === 'string' &&
      ['user', 'bot'].includes(message.role) &&
      ['text', 'skeleton'].includes(message.type)
    );
  });

  return validMessages.filter(
    (message) => message.type !== 'skeleton' && message.id !== 'welcome'
  );
};

const getStoredChatState = (labels, limits) => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);

    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw);

    if (!parsed || typeof parsed !== 'object') {
      return null;
    }

    return {
      isOpen: Boolean(parsed.isOpen),
      messages: normalizeMessages(parsed.messages, labels),
      messageHistory: normalizeMessageHistory(
        parsed.messageHistory,
        limits.maxHistoryItems
      ),
      draft:
        typeof parsed.draft === 'string'
          ? truncatePromptText(
            normalizePromptText(parsed.draft),
            limits.maxDraftLength
          )
          : '',
      pageContextEnabled:
        typeof parsed.pageContextEnabled === 'boolean'
          ? parsed.pageContextEnabled
          : null,
      sessionId:
        typeof parsed.sessionId === 'string' && parsed.sessionId !== ''
          ? parsed.sessionId
          : generateSessionId(),
    };
  } catch (error) {
    console.warn('Unable to read chatbot session state', error);
    return null;
  }
};

const saveChatState = (state) => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch (error) {
    console.warn('Unable to save chatbot session state', error);
  }
};

const clearChatState = () => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.warn('Unable to clear chatbot session state', error);
  }
};

const getFocusableElements = (container) => {
  if (!container) {
    return [];
  }

  return Array.from(
    container.querySelectorAll(
      [
        'a[href]',
        'button:not([disabled])',
        'textarea:not([disabled])',
        'input:not([disabled])',
        'select:not([disabled])',
        '[contenteditable="true"]',
        '[tabindex]:not([tabindex="-1"])',
      ].join(',')
    )
  ).filter((element) => {
    return Boolean(
      element.offsetWidth ||
      element.offsetHeight ||
      element.getClientRects().length
    );
  });
};

const parseCtas = (text) => {
  const ctaBlockRegex = /<ctas>([\s\S]*?)<\/ctas>/i;
  const match = ctaBlockRegex.exec(text || '');

  if (!match) {
    return { cleanText: text || '', ctas: [] };
  }

  const cleanText = text.replace(ctaBlockRegex, '').trim();
  const rawCtas = match[1];

  const ctaRegex = /<cta>([\s\S]*?)<\/cta>/gi;
  const ctas = [];
  let ctaMatch;

  while ((ctaMatch = ctaRegex.exec(rawCtas)) !== null) {
    const ctaText = ctaMatch[1].trim();

    if (ctaText) {
      ctas.push(ctaText);
    }
  }

  return {
    cleanText,
    ctas: ctas.slice(0, 3),
  };
};

const ChatBot = () => {
  const labels = useMemo(() => getLabels(), []);
  const limits = useMemo(() => getLimits(), []);
  const pageContextConfig = useMemo(() => getPageContextConfig(), []);
  const experienceConfig = useMemo(() => getExperienceConfig(), []);
  const appearanceConfig = useMemo(() => getAppearanceConfig(), []);
  const defaultPageContextEnabled =
    pageContextConfig.allowed && pageContextConfig.strategy !== 'never';

  const [isOpen, setIsOpen] = useState(false);
  const [storageActivated, setStorageActivated] = useState(false);
  const [sessionId, setSessionId] = useState(generateSessionId);
  const [isSending, setIsSending] = useState(false);
  const [draft, setDraft] = useState('');
  const [messageHistory, setMessageHistory] = useState([]);
  const [messages, setMessages] = useState([]);
  const [experienceMode, setExperienceMode] = useState(EXPERIENCE_FIRST_RUN);
  const [showIntroOverride, setShowIntroOverride] = useState(false);
  const [persistIntro, setPersistIntro] = useState(false);
  const [pageContextEnabled, setPageContextEnabled] = useState(
    defaultPageContextEnabled
  );
  const [presenceState, setPresenceState] = useState('idle');
  const [introBeat, setIntroBeat] = useState(0);
  const [introInteractionPaused, setIntroInteractionPaused] = useState(false);
  const [isIntroExiting, setIsIntroExiting] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const [documentVisible, setDocumentVisible] = useState(true);
  const [liveAnnouncement, setLiveAnnouncement] = useState('');
  const [isTransparencyOpen, setIsTransparencyOpen] = useState(false);
  const [isPromptFocused, setIsPromptFocused] = useState(false);

  const dialogRef = useRef(null);
  const introRef = useRef(null);
  const introStartRef = useRef(null);
  const formRef = useRef(null);
  const promptRef = useRef(null);
  const messagesRef = useRef(null);
  const transparencyRef = useRef(null);
  const previouslyFocusedElementRef = useRef(null);
  const storageActivatedRef = useRef(false);
  const isComposingRef = useRef(false);
  const lastUserMessageIdRef = useRef(null);
  const lastUserTextRef = useRef('');
  const pendingScrollMessageIdRef = useRef(null);
  const messagesScrollTopRef = useRef(null);
  const scrollObserverRef = useRef(null);
  const scrollTimeoutsRef = useRef([]);
  const presenceTimerRef = useRef(null);
  const introTimerRef = useRef(null);
  const introExitCompletedRef = useRef(false);
  const liveAnnouncementTimerRef = useRef(null);

  const hasConversation =
    messages.some((message) => message.role === 'user') ||
    messageHistory.length > 0;
  const renderedPresenceState = isSending
    ? 'thinking'
    : draft.trim()
      ? 'composing'
      : presenceState === 'idle' && isPromptFocused
        ? 'listening'
        : presenceState;
  const effectsEnabled = documentVisible && !prefersReducedMotion;
  const composerState = isSending
    ? 'loading'
    : draft.trim()
      ? 'ready'
      : presenceState === 'settled' ? 'success' : presenceState;
  const currentView = showIntroOverride
    ? EXPERIENCE_FIRST_RUN
    : hasConversation
      ? EXPERIENCE_CONVERSATION
      : experienceMode;
  const activeIntroBeat = INTRO_BEATS[introBeat] || INTRO_BEATS[0];
  const introActionLabel = introBeat >= INTRO_BEATS.length - 1 ? 'Inizia a chiedere' : 'Avanti';

  const syncPromptDom = (value, shouldFocus = false) => {
    const promptElement = promptRef.current;

    if (!promptElement) {
      return;
    }

    setEditableText(promptElement, value);

    if (shouldFocus) {
      promptElement.focus();
      moveCaretToEnd(promptElement);
    }
  };

  const setPromptValue = (value, shouldFocus = false) => {
    const normalized = truncatePromptText(
      normalizePromptText(value),
      limits.maxDraftLength
    );

    setDraft(normalized);
    syncPromptDom(normalized, shouldFocus);
  };

  const closeTransparency = () => {
    if (transparencyRef.current) {
      transparencyRef.current.open = false;
    }

    setIsTransparencyOpen(false);
  };

  const openChatbot = () => {
    if (typeof document !== 'undefined') {
      previouslyFocusedElementRef.current = document.activeElement;
    }

    if (!storageActivatedRef.current) {
      const sessionStorage = getBrowserStorage('sessionStorage', window);
      const localStorage = experienceConfig.persistenceEnabled
        ? getBrowserStorage('localStorage', window)
        : null;
      const stored = getStoredChatState(labels, limits);
      const storedMessages = stored?.messages || [];
      const storedHistory = stored?.messageHistory || [];
      const storedConversation =
        storedMessages.some((message) => message.role === 'user') ||
        storedHistory.length > 0;
      const completedForSession = readSessionOnboarding(sessionStorage);
      const completedForDevice =
        experienceConfig.persistenceEnabled &&
        readOnboardingPreference(localStorage);

      setMessages(storedMessages);
      setMessageHistory(storedHistory);
      setDraft(stored?.draft || '');
      if (stored?.sessionId) {
        setSessionId(stored.sessionId);
      }
      setPageContextEnabled(
        stored?.pageContextEnabled ?? defaultPageContextEnabled
      );
      setPersistIntro(Boolean(completedForDevice));
      setExperienceMode(
        storedConversation
          ? EXPERIENCE_CONVERSATION
          : completedForSession || completedForDevice
            ? EXPERIENCE_RETURNING
            : EXPERIENCE_FIRST_RUN
      );
      storageActivatedRef.current = true;
      setStorageActivated(true);
    }

    setShowIntroOverride(false);
    setPresenceState('inviting');
    setIsOpen(true);
  };

  const closeChatbot = () => {
    if (messagesRef.current) {
      messagesScrollTopRef.current = messagesRef.current.scrollTop;
    }

    clearScheduledMessageScroll();
    closeTransparency();
    setIsPromptFocused(false);
    setIsOpen(false);
  };

  const handleCtaClick = (ctaText) => {
    setPromptValue(ctaText, true);
  };

  const completeIntroForSession = () => {
    saveSessionOnboarding(getBrowserStorage('sessionStorage', window));
    setExperienceMode(EXPERIENCE_RETURNING);
    setShowIntroOverride(false);
  };

  const handleIntroContinue = () => {
    if (isIntroExiting) {
      return;
    }

    window.clearTimeout(introTimerRef.current);
    setIntroInteractionPaused(false);
    introExitCompletedRef.current = false;
    setIsIntroExiting(true);
  };

  const finishIntroContinue = () => {
    if (!isIntroExiting || introExitCompletedRef.current) {
      return;
    }

    introExitCompletedRef.current = true;
    completeIntroForSession();
  };

  const handleReplayIntro = () => {
    window.clearTimeout(introTimerRef.current);
    closeTransparency();
    setIntroBeat(prefersReducedMotion ? INTRO_BEATS.length - 1 : 0);
    setIntroInteractionPaused(false);
    setIsIntroExiting(false);
    introExitCompletedRef.current = false;
    setPresenceState('inviting');
    setShowIntroOverride(true);
  };

  const handleShowIntroAgain = () => {
    clearSessionOnboarding(getBrowserStorage('sessionStorage', window));

    if (experienceConfig.persistenceEnabled) {
      clearOnboardingPreference(getBrowserStorage('localStorage', window));
    }

    setPersistIntro(false);
    setExperienceMode(EXPERIENCE_FIRST_RUN);
    handleReplayIntro();
  };

  const advanceIntro = () => {
    if (introBeat >= INTRO_BEATS.length - 1) {
      handleIntroContinue();
      return;
    }

    setIntroBeat((current) => Math.min(current + 1, INTRO_BEATS.length - 1));
  };

  const handlePersistenceChange = (event) => {
    const shouldPersist = event.target.checked;
    const storage = getBrowserStorage('localStorage', window);

    if (!storage) {
      setPersistIntro(false);
      return;
    }

    const updated = shouldPersist
      ? saveOnboardingPreference(
        storage,
        experienceConfig.persistenceMaxAgeDays
      )
      : clearOnboardingPreference(storage);

    setPersistIntro(updated ? shouldPersist : false);
  };

  const clearScheduledMessageScroll = () => {
    scrollTimeoutsRef.current.forEach((timeoutId) => {
      window.clearTimeout(timeoutId);
    });

    scrollTimeoutsRef.current = [];

    if (scrollObserverRef.current) {
      scrollObserverRef.current.disconnect();
      scrollObserverRef.current = null;
    }
  };

  const getMessageElementById = (messageId) => {
    const container = messagesRef.current;

    if (!container || !messageId) {
      return null;
    }

    return Array.from(
      container.querySelectorAll('[data-chatbot-message-id]')
    ).find((element) => {
      return element.dataset.chatbotMessageId === messageId;
    });
  };

  const scrollToMessageStart = (messageId, behavior = 'smooth') => {
    const container = messagesRef.current;
    const messageElement = getMessageElementById(messageId);

    if (!container || !messageElement) {
      return false;
    }

    const containerRect = container.getBoundingClientRect();
    const messageRect = messageElement.getBoundingClientRect();

    const SCROLL_TOP_OFFSET = 12;

    const nextScrollTop = container.scrollTop + (messageRect.top - containerRect.top) - SCROLL_TOP_OFFSET;

    container.scrollTo({
      top: Math.max(0, nextScrollTop),
      behavior,
    });

    return true;
  };

  const scheduleScrollToMessageStart = (messageId) => {
    if (!messageId || typeof window === 'undefined') {
      return;
    }

    const container = messagesRef.current;

    if (!container) {
      return;
    }

    clearScheduledMessageScroll();

    const runScroll = () => {
      scrollToMessageStart(
        messageId,
        prefersReducedMotion ? 'auto' : 'smooth'
      );
    };

    window.requestAnimationFrame(() => {
      runScroll();

      window.requestAnimationFrame(() => {
        runScroll();
      });
    });

    [80, 180, 360, 700].forEach((delay) => {
      const timeoutId = window.setTimeout(runScroll, delay);
      scrollTimeoutsRef.current.push(timeoutId);
    });

    scrollObserverRef.current = new MutationObserver(() => {
      runScroll();
    });

    scrollObserverRef.current.observe(container, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    const disconnectTimeoutId = window.setTimeout(() => {
      if (scrollObserverRef.current) {
        scrollObserverRef.current.disconnect();
        scrollObserverRef.current = null;
      }
    }, 900);

    scrollTimeoutsRef.current.push(disconnectTimeoutId);
  };

  const queueScrollToUserMessage = (messageId) => {
    const container = messagesRef.current;
    const distanceFromBottom = container
      ? container.scrollHeight - container.scrollTop - container.clientHeight
      : 0;

    pendingScrollMessageIdRef.current =
      distanceFromBottom <= 96 ? messageId : null;
  };

  const announceLive = (message) => {
    window.clearTimeout(liveAnnouncementTimerRef.current);
    setLiveAnnouncement('');

    window.requestAnimationFrame(() => {
      setLiveAnnouncement(message);
      liveAnnouncementTimerRef.current = window.setTimeout(
        () => setLiveAnnouncement(''),
        2400
      );
    });
  };

  useEffect(() => {
    if (!storageActivated) {
      return;
    }

    saveChatState({
      messages,
      messageHistory,
      draft,
      pageContextEnabled,
      sessionId,
    });
  }, [
    storageActivated,
    messages,
    messageHistory,
    draft,
    pageContextEnabled,
    sessionId,
  ]);

  useEffect(() => {
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const syncMotionPreference = () => setPrefersReducedMotion(media.matches);
    const syncVisibility = () => setDocumentVisible(!document.hidden);

    syncMotionPreference();
    syncVisibility();
    media.addEventListener?.('change', syncMotionPreference);
    document.addEventListener('visibilitychange', syncVisibility);

    return () => {
      media.removeEventListener?.('change', syncMotionPreference);
      document.removeEventListener('visibilitychange', syncVisibility);
    };
  }, []);

  useEffect(() => {
    window.clearTimeout(introTimerRef.current);

    if (!isOpen || currentView !== EXPERIENCE_FIRST_RUN) {
      return undefined;
    }

    setIntroBeat(prefersReducedMotion ? INTRO_BEATS.length - 1 : 0);
    setIntroInteractionPaused(false);
    setIsIntroExiting(false);
    introExitCompletedRef.current = false;

    return () => window.clearTimeout(introTimerRef.current);
  }, [isOpen, currentView, prefersReducedMotion]);

  useEffect(() => {
    if (
      !isOpen ||
      currentView !== EXPERIENCE_FIRST_RUN ||
      prefersReducedMotion ||
      !documentVisible ||
      introInteractionPaused ||
      introBeat >= INTRO_BEATS.length - 1
    ) {
      return undefined;
    }

    introTimerRef.current = window.setTimeout(() => {
      setIntroBeat((current) => Math.min(current + 1, INTRO_BEATS.length - 1));
    }, INTRO_BEAT_DELAYS[introBeat]);

    return () => window.clearTimeout(introTimerRef.current);
  }, [
    isOpen,
    currentView,
    introBeat,
    introInteractionPaused,
    prefersReducedMotion,
    documentVisible,
  ]);

  useEffect(() => {
    if (
      !isOpen ||
      !['inviting', 'settled'].includes(presenceState)
    ) {
      return undefined;
    }

    presenceTimerRef.current = window.setTimeout(
      () => setPresenceState('idle'),
      prefersReducedMotion ? 100 : presenceState === 'inviting' ? 900 : 1800
    );

    return () => window.clearTimeout(presenceTimerRef.current);
  }, [isOpen, presenceState, prefersReducedMotion]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const promptElement = promptRef.current;

    if (!promptElement) {
      return;
    }

    const currentText = getEditableText(promptElement);

    if (currentText !== draft) {
      setEditableText(promptElement, draft);
    }
  }, [isOpen, draft]);

  useEffect(() => {
    const openEventName = getOpenEventName();

    const handleOpenEvent = () => {
      openChatbot();
    };

    const handleTriggerClick = (event) => {
      const target = event.target instanceof Element ? event.target : null;

      if (!target) {
        return;
      }

      const trigger = target.closest(TRIGGER_SELECTOR);

      if (!trigger) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      openChatbot();
    };

    const handleGlobalShortcuts = (event) => {
      const target = event.target instanceof Element ? event.target : null;
      const isEditing = target?.closest(
        'input, textarea, select, [contenteditable="true"], [role="textbox"], [role="combobox"]'
      );
      const isMacShortcut = event.metaKey && event.key.toLowerCase() === 'k';
      const isWinShortcut = event.ctrlKey && event.key.toLowerCase() === 'k';

      if (
        event.defaultPrevented ||
        isEditing ||
        (!isMacShortcut && !isWinShortcut)
      ) {
        return;
      }

      event.preventDefault();
      openChatbot();
    };

    window.addEventListener(openEventName, handleOpenEvent);
    document.addEventListener('click', handleTriggerClick, true);
    document.addEventListener('keydown', handleGlobalShortcuts);

    return () => {
      window.removeEventListener(openEventName, handleOpenEvent);
      document.removeEventListener('click', handleTriggerClick, true);
      document.removeEventListener('keydown', handleGlobalShortcuts);
    };
  }, []);

  useEffect(() => {
    if (!isOpen) {
      document.body.classList.remove(BODY_OPEN_CLASS);

      const previous = previouslyFocusedElementRef.current;

      if (previous && typeof previous.focus === 'function') {
        window.requestAnimationFrame(() => {
          previous.focus();
        });
      }

      return undefined;
    }

    document.body.classList.add(BODY_OPEN_CLASS);

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeChatbot();
        return;
      }

      if (event.key !== 'Tab') {
        return;
      }

      const focusableElements = getFocusableElements(dialogRef.current);

      if (focusableElements.length === 0) {
        event.preventDefault();
        dialogRef.current?.focus();
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
        return;
      }

      if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.body.classList.remove(BODY_OPEN_CLASS);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    window.requestAnimationFrame(() => {
      if (currentView === EXPERIENCE_FIRST_RUN) {
        if (introRef.current) {
          introRef.current.scrollTop = 0;
        }
        dialogRef.current?.focus({ preventScroll: true });
        return;
      }

      if (currentView === EXPERIENCE_CONVERSATION) {
        dialogRef.current?.focus({ preventScroll: true });

        if (messagesScrollTopRef.current !== null && messagesRef.current) {
          messagesRef.current.scrollTo({
            top: messagesScrollTopRef.current,
            behavior: 'auto',
          });
        }

        return;
      }

      dialogRef.current?.focus({ preventScroll: true });
    });
  }, [isOpen, currentView]);

  useEffect(() => {
    const messageId = pendingScrollMessageIdRef.current;

    if (!messageId) {
      return;
    }

    pendingScrollMessageIdRef.current = null;
    scheduleScrollToMessageStart(messageId);
  }, [messages]);

  useEffect(() => {
    return () => {
      clearScheduledMessageScroll();
      window.clearTimeout(presenceTimerRef.current);
      window.clearTimeout(introTimerRef.current);
      window.clearTimeout(liveAnnouncementTimerRef.current);
    };
  }, []);

  const addToHistory = (userMessage, botResponse) => {
    setMessageHistory((previous) => {
      const newHistory = [
        ...previous,
        { role: 'user', content: userMessage },
        { role: 'assistant', content: botResponse },
      ];

      return newHistory.slice(-limits.maxHistoryItems);
    });
  };

  const removeSkeletonThen = (updater) => {
    setMessages((previous) =>
      updater(previous.filter((message) => message.id !== 'skeleton'))
    );
  };

  const appendErrorMessage = (
    text = 'Si è verificato un problema nel recupero della risposta. Riprova tra poco.'
  ) => {
    removeSkeletonThen((previous) => [
      ...previous,
      {
        id: `bot-error-${Date.now()}`,
        role: 'bot',
        type: 'text',
        text,
        isError: true,
        retryText: lastUserTextRef.current,
      },
    ]);
    setPresenceState('error');
    announceLive(
      'Non è stato possibile recuperare la risposta. Puoi riprovare.'
    );
  };

  const appendApiResponse = (payload) => {
    const replyText = repairCommonMojibake(
      payload?.reply ||
      payload?.message ||
      payload?.text ||
      ''
    ).trim();

    const fallbackReply =
      'Ho ricevuto la richiesta ma non ci sono dettagli da mostrarti.';
    const isErrorResponse = !payload?.response_mode;

    removeSkeletonThen((previous) => [
      ...previous,
      {
        id: `bot-reply-${Date.now()}`,
        role: 'bot',
        type: 'text',
        text: replyText || fallbackReply,
        isError: isErrorResponse,
        retryText: isErrorResponse ? lastUserTextRef.current : undefined,
      },
    ]);
    setPresenceState(isErrorResponse ? 'error' : 'settled');
    announceLive(isErrorResponse
      ? 'Non è stato possibile recuperare la risposta. Puoi riprovare.'
      : 'Nuova risposta dell’assistente disponibile.'
    );
  };

  const resetChat = () => {
    closeTransparency();
    setMessages([]);
    setMessageHistory([]);
    setPromptValue('', true);
    setIsSending(false);
    setPresenceState('idle');
    setLiveAnnouncement('');
    setPageContextEnabled(defaultPageContextEnabled);
    setExperienceMode(EXPERIENCE_RETURNING);
    setShowIntroOverride(false);
    setSessionId(generateSessionId());
    clearChatState();

    window.requestAnimationFrame(() => dialogRef.current?.focus({ preventScroll: true }));
  };

  const handlePromptInput = () => {
    const promptElement = promptRef.current;

    if (!promptElement) {
      return;
    }

    let nextDraft = getEditableText(promptElement);

    if (nextDraft.length > limits.maxDraftLength) {
      nextDraft = truncatePromptText(nextDraft, limits.maxDraftLength);
      setEditableText(promptElement, nextDraft);
      moveCaretToEnd(promptElement);
    }

    setDraft(nextDraft);
  };

  const handlePromptBeforeInput = (event) => {
    if (isSending) {
      event.preventDefault();
      return;
    }

    if (
      typeof event.inputType === 'string' &&
      event.inputType.startsWith('delete')
    ) {
      return;
    }

    const currentText = draft || '';
    const incomingText = event.data || '';

    if (
      incomingText &&
      currentText.length >= limits.maxDraftLength &&
      window.getSelection()?.toString() === ''
    ) {
      event.preventDefault();
    }
  };

  const handlePromptPaste = (event) => {
    if (isSending) {
      event.preventDefault();
      return;
    }

    const plainText = event.clipboardData?.getData('text/plain') || '';

    if (!plainText) {
      return;
    }

    event.preventDefault();

    insertPlainTextAtSelection(promptRef.current, plainText);

    let nextDraft = getEditableText(promptRef.current);
    nextDraft = truncatePromptText(nextDraft, limits.maxDraftLength);

    setEditableText(promptRef.current, nextDraft);
    moveCaretToEnd(promptRef.current);
    setDraft(nextDraft);
  };

  const handlePromptKeyDown = (event) => {
    if (
      event.key === 'Enter' &&
      !event.shiftKey &&
      !isComposingRef.current
    ) {
      event.preventDefault();

      if (!isSending && draft.trim()) {
        formRef.current?.requestSubmit();
      }
    }
  };

  const handlePromptCompositionStart = () => {
    isComposingRef.current = true;
  };

  const handlePromptCompositionEnd = () => {
    isComposingRef.current = false;
  };

  const handleSend = async (event) => {
    event.preventDefault();

    const trimmed = draft.trim();

    if (!trimmed || isSending) {
      return;
    }

    if (trimmed.length > limits.maxDraftLength) {
      setMessages((previous) => [
        ...previous,
        {
          id: `bot-message-too-long-${Date.now()}`,
          role: 'bot',
          type: 'text',
          text: `Il messaggio è troppo lungo. Scrivimi una richiesta più breve, massimo ${limits.maxDraftLength} caratteri.`,
        },
      ]);
      return;
    }

    const userMessagesCount = messages.filter(
      (message) => message.role === 'user'
    ).length;

    if (userMessagesCount >= limits.maxUserMessagesPerSession) {
      setMessages((previous) => [
        ...previous,
        {
          id: `bot-session-limit-${Date.now()}`,
          role: 'bot',
          type: 'text',
          text: 'Hai raggiunto il limite massimo di messaggi per questa sessione. Avvia una nuova chat o riprova più tardi.',
        },
      ]);
      return;
    }

    const isFirstUserMessage = userMessagesCount === 0 && messageHistory.length === 0;
    const shouldIncludePageContextText =
      pageContextEnabled &&
      pageContextConfig.allowed &&
      (
        pageContextConfig.strategy === 'always' ||
        (
          pageContextConfig.strategy === 'first_message_only' &&
          isFirstUserMessage
        )
      );

    const userMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      type: 'text',
      text: trimmed,
    };

    lastUserMessageIdRef.current = userMessage.id;
    queueScrollToUserMessage(userMessage.id);

    setMessages((previous) => [
      ...previous,
      userMessage,
      {
        id: 'skeleton',
        role: 'bot',
        type: 'skeleton',
      },
    ]);

    setPromptValue('');
    setIsSending(true);

    try {
      const { data: payload } = await axios.post(
        getEndpoint(),
        {
          message: trimmed,
          session_id: sessionId,
          history: messageHistory,
          page_context: createPageContext({
            includeText: shouldIncludePageContextText,
            maxChars: limits.maxPageContextChars,
          }),
          page_context_meta: {
            strategy: pageContextConfig.strategy,
            included: shouldIncludePageContextText,
            first_message: isFirstUserMessage,
          },
        },
        {
          headers: {
            Accept: 'application/json',
          },
        }
      );

      const historyReplyText = repairCommonMojibake(
        payload?.reply ||
        payload?.message ||
        payload?.text ||
        'Ho ricevuto la richiesta ma non ci sono dettagli da mostrarti.'
      );

      appendApiResponse(payload);
      addToHistory(trimmed, historyReplyText);
    } catch (error) {
      if (getFrontendConfig().env === 'dev') {
        console.error('ChatBot request failed', {
          status: error?.response?.status || null,
          code: error?.code || 'request_failed',
        });
      }

      const backendPayload = error?.response?.data;
      const backendReply =
        backendPayload?.reply ||
        backendPayload?.message ||
        backendPayload?.text;

      if (typeof backendReply === 'string' && backendReply.trim() !== '') {
        appendApiResponse({
          reply: backendReply,
        });
      } else {
        appendErrorMessage();
      }
    } finally {
      setIsSending(false);

      window.requestAnimationFrame(() => {
        promptRef.current?.focus();
      });
    }
  };

  const handleExperienceSubmit = (event) => {
    if (draft.trim() && !isSending) {
      lastUserTextRef.current = draft.trim();
    }

    if (currentView === EXPERIENCE_FIRST_RUN && draft.trim()) {
      completeIntroForSession();
    }

    handleSend(event);
  };

  if (!isOpen) {
    return null;
  }

  const contextCanBeSent =
    pageContextConfig.allowed &&
    pageContextConfig.strategy !== 'never' &&
    (
      pageContextConfig.strategy === 'always' ||
      !hasConversation
    );
  const contextStatus = !pageContextConfig.allowed ||
    pageContextConfig.strategy === 'never'
    ? 'Contesto pagina: testo non usato'
    : pageContextConfig.strategy === 'first_message_only' && hasConversation
      ? 'Contesto pagina: testo non più usato'
      : pageContextEnabled
        ? 'Contesto pagina: attivo'
        : 'Contesto pagina: testo non usato';

  const actionRows = (
    <m.div className="chatbot-action-list" aria-label="Domande suggerite">
      {INTRO_ACTIONS.map((action, index) => (
        <BeamButton
          as={m.button}
          animated={effectsEnabled}
          key={action.label}
          type="button"
          className="chatbot-action-row"
          onClick={() => handleCtaClick(action.prompt)}
          disabled={isSending}
          initial={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{
            duration: prefersReducedMotion ? 0.1 : 0.52,
            delay: prefersReducedMotion ? 0 : 0.38 + index * 0.16,
            ease: [0.16, 1, 0.3, 1],
          }}
        >
          <span className="chatbot-action-row__icon" aria-hidden="true">
            <i className={`bi ${action.icon}`} />
          </span>
          <span className="chatbot-action-row__copy">
            <strong>{action.label}</strong>
            <small>{action.description}</small>
          </span>
          <i className="bi bi-arrow-right" aria-hidden="true" />
        </BeamButton>
      ))}
    </m.div>
  );

  return (
    <LazyMotion features={domAnimation} strict>
      <m.div
        className="kapusons-chatbot chatbot-modal"
        data-view={currentView}
        data-intro-beat={introBeat}
        role="presentation"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{
          duration: prefersReducedMotion
            ? 0.1
            : currentView === EXPERIENCE_FIRST_RUN
              ? 0.78
              : 0.32,
          ease: [0.16, 1, 0.3, 1],
        }}
        onMouseDown={(event) => {
          if (event.target === event.currentTarget) {
            closeChatbot();
          }
        }}
      >
        <m.section
          ref={dialogRef}
          className="chatbot-modal__dialog"
          role="dialog"
          aria-modal="true"
          aria-labelledby={
            currentView === EXPERIENCE_FIRST_RUN ? INTRO_TITLE_ID : DIALOG_TITLE_ID
          }
          tabIndex={-1}
          initial={prefersReducedMotion
            ? { opacity: 0 }
            : { opacity: 0, y: 24, scale: 0.972 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{
            duration: prefersReducedMotion
              ? 0.1
              : currentView === EXPERIENCE_FIRST_RUN
                ? 0
                : 0.42,
            ease: [0.16, 1, 0.3, 1],
          }}
        >
          <div className="chatbot-stage" aria-hidden="true">
            <div className="chatbot-stage__atmosphere" />
            <div className="chatbot-stage__grain" />
          </div>
          <header className="chatbot-modal__header">
            <div className="chatbot-modal__identity">
              <AgentPresence
                state={renderedPresenceState}
                size="compact"
                colors={appearanceConfig.strandColors}
                reducedMotion={prefersReducedMotion}
                paused={!documentVisible}
              />

              <div className="chatbot-modal__heading">
                <span className="chatbot-modal__ai-label">INAPP PIAAC</span>
                <h2 id={DIALOG_TITLE_ID} className="chatbot-modal__title">
                  Assistente IA
                </h2>
              </div>
            </div>

            <div className="chatbot-modal__actions">
              {currentView !== EXPERIENCE_FIRST_RUN ? (
                <BeamButton
                  animated={effectsEnabled}
                  type="button"
                  className="chatbot-modal__button"
                  aria-label="Rivedi l’introduzione"
                  title="Rivedi l’introduzione"
                  onClick={handleReplayIntro}
                  disabled={isSending}
                >
                  <i className="bi bi-info-lg" aria-hidden="true" />
                </BeamButton>
              ) : null}

              <BeamButton
                animated={effectsEnabled}
                type="button"
                className="chatbot-modal__button chatbot-modal__button--close"
                title={labels.closeTitle}
                aria-label={labels.closeTitle}
                onClick={closeChatbot}
              >
                <i className="bi bi-x-lg" aria-hidden="true" />
              </BeamButton>
            </div>
          </header>

          <AnimatePresence mode="wait">
            {currentView === EXPERIENCE_FIRST_RUN ? (
              <m.main
                key={EXPERIENCE_FIRST_RUN}
                ref={introRef}
                className="chatbot-intro"
                initial={false}
                animate={{ opacity: isIntroExiting ? 0 : 1 }}
                exit={{
                  opacity: 0,
                  transition: {
                    duration: prefersReducedMotion || isIntroExiting ? 0 : INTRO_BEAT_TRANSITION_SECONDS,
                    ease: [0.7, 0, 0.84, 0],
                  },
                }}
                transition={{
                  duration: prefersReducedMotion ? 0.1 : INTRO_BEAT_TRANSITION_SECONDS,
                  ease: isIntroExiting ? [0.7, 0, 0.84, 0] : [0.16, 1, 0.3, 1],
                }}
                onAnimationComplete={finishIntroContinue}
              >
                <div className="chatbot-intro__visual">
                  <div className="chatbot-agent-control" aria-hidden="true">
                    <AgentPresence
                      state="idle"
                      size="hero"
                      colors={appearanceConfig.strandColors}
                      reducedMotion={prefersReducedMotion}
                      paused={!documentVisible}
                    />
                  </div>
                </div>

                <div
                  className="chatbot-intro__content"
                  onPointerEnter={(event) => {
                    if (event.pointerType !== 'touch') {
                      setIntroInteractionPaused(true);
                    }
                  }}
                  onPointerLeave={() => setIntroInteractionPaused(false)}
                  onFocusCapture={() => setIntroInteractionPaused(true)}
                  onBlurCapture={() => setIntroInteractionPaused(false)}
                >
                  <AnimatePresence mode="wait">
                    <m.div
                      key={introBeat}
                      className="chatbot-intro__beat"
                      initial={prefersReducedMotion
                        ? { opacity: 0 }
                        : { opacity: 0, y: 24, filter: 'blur(12px)' }}
                      animate={prefersReducedMotion
                        ? { opacity: 1 }
                        : { opacity: 1, y: 0, filter: 'blur(0px)' }}
                      exit={prefersReducedMotion
                        ? { opacity: 0 }
                        : { opacity: 0, y: -20, filter: 'blur(10px)' }}
                      transition={{
                        duration: prefersReducedMotion ? 0.1 : INTRO_BEAT_TRANSITION_SECONDS,
                        ease: [0.65, 0, 0.35, 1],
                      }}
                    >
                      <h3
                        id={INTRO_TITLE_ID}
                        className="chatbot-intro__title"
                        tabIndex={-1}
                      >
                        {activeIntroBeat.title}
                      </h3>
                    </m.div>
                  </AnimatePresence>

                  <p className="chatbot-intro__lede">
                    Fai domande, esplora i temi dell’indagine e orientati tra i
                    materiali INAPP. L’assistente può sbagliare: verifica sempre
                    le informazioni importanti.
                  </p>

                  <p
                    id="kapusons-ai-chatbot-intro-summary"
                    className="visually-hidden"
                  >
                    Sono l’assistente IA di PIAAC. Posso spiegare contenuti e fonti,
                    aiutarti a capire da dove iniziare e cosa approfondire. Posso
                    sbagliare: la scelta e le verifiche restano tue.
                  </p>

                  <AnimatePresence>
                    {introBeat >= INTRO_BEATS.length - 1 ? (
                      <m.ul
                        className="chatbot-intro__capabilities"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{
                          duration: prefersReducedMotion ? 0.1 : INTRO_BEAT_TRANSITION_SECONDS,
                          ease: [0.16, 1, 0.3, 1],
                        }}
                      >
                        {INTRO_CAPABILITIES.map((capability) => (
                          <li key={capability.title}>
                            <i className={`bi ${capability.icon}`} aria-hidden="true" />
                            <strong>{capability.title}</strong>
                            <span>{capability.description}</span>
                          </li>
                        ))}
                      </m.ul>
                    ) : null}
                  </AnimatePresence>

                  <div className="chatbot-intro__actions-slot">
                    <BeamButton
                      animated={effectsEnabled}
                      ref={introStartRef}
                      type="button"
                      className="chatbot-button chatbot-button--primary"
                      aria-label={introActionLabel}
                      aria-describedby="kapusons-ai-chatbot-intro-summary"
                      onClick={advanceIntro}
                      disabled={isIntroExiting}
                    >
                      <span className="chatbot-intro__next-label" aria-hidden="true">
                        <AnimatePresence mode="wait" initial={false}>
                          <m.span
                            key={introActionLabel}
                            initial={prefersReducedMotion ? false : { opacity: 0, y: 6, filter: 'blur(4px)' }}
                            animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                            exit={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: -6, filter: 'blur(4px)' }}
                            transition={{ duration: prefersReducedMotion ? 0 : 0.22, ease: [0.16, 1, 0.3, 1] }}
                          >
                            {introActionLabel}
                          </m.span>
                        </AnimatePresence>
                      </span>
                    </BeamButton>
                  </div>
                </div>
              </m.main>
            ) : (
              <m.main
                key={currentView}
                className="chatbot-conversation"
                initial={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{
                  duration: prefersReducedMotion ? 0.1 : 0.58,
                  ease: [0.16, 1, 0.3, 1],
                }}
                onAnimationComplete={() => {
                  setIsIntroExiting(false);
                  introExitCompletedRef.current = false;

                  if (currentView === EXPERIENCE_RETURNING) {
                    dialogRef.current?.focus({ preventScroll: true });
                  }
                }}
              >
                {currentView === EXPERIENCE_RETURNING ? (
                  <section className="chatbot-welcome" aria-labelledby="chatbot-welcome-title">
                    <div className="chatbot-welcome__hero">
                      <div className="chatbot-welcome__copy">
                        <h3 id="chatbot-welcome-title">
                          Ciao, sono il tuo<br />Assistente IA
                        </h3>
                        <p>
                          Posso aiutarti con informazioni, concetti e
                          approfondimenti sull’indagine PIAAC.
                        </p>
                        <div className="chatbot-welcome__actions">
                          <BeamButton
                            animated={effectsEnabled}
                            type="button"
                            className="chatbot-button chatbot-button--primary"
                            onClick={() => syncPromptDom(draft, true)}
                          >
                            <span>Fai una domanda</span>
                          </BeamButton>
                          <BeamButton
                            animated={effectsEnabled}
                            type="button"
                            className="chatbot-welcome__info"
                            onClick={handleReplayIntro}
                          >
                            Scopri cosa posso fare
                            <i className="bi bi-chevron-down" aria-hidden="true" />
                          </BeamButton>
                        </div>
                      </div>

                      <div className="chatbot-welcome__ambient" aria-hidden="true">
                        <div className="chatbot-welcome__curve" />
                        <AgentPresence
                          state="idle"
                          size="hero"
                          colors={appearanceConfig.strandColors}
                          reducedMotion={prefersReducedMotion}
                          paused={!documentVisible}
                        />
                      </div>
                    </div>

                    {actionRows}
                  </section>
                ) : null}

                {messages.length > 0 ? (
                  <ol
                    ref={messagesRef}
                    className="chatbot-modal__messages"
                    aria-label="Cronologia della conversazione"
                    onWheel={clearScheduledMessageScroll}
                    onTouchMove={clearScheduledMessageScroll}
                  >
                    {messages.map((message, messageIndex) => {
                      const authorId = `kapusons-ai-chatbot-message-${messageIndex}-author`;
                      const ctaLabelId = `kapusons-ai-chatbot-message-${messageIndex}-ctas`;

                      return (
                        <li
                          key={message.id}
                          data-chatbot-message-id={message.id}
                          data-chatbot-message-role={message.role}
                          className={[
                            'chatbot-message',
                            `chatbot-message--${message.role}`,
                            message.type === 'skeleton'
                              ? 'chatbot-message--skeleton'
                              : '',
                            message.isError ? 'chatbot-message--error' : '',
                          ]
                            .filter(Boolean)
                            .join(' ')}
                        >
                          {message.type === 'skeleton' ? (
                            <div className="chatbot-waiting" role="status">
                              <span className="visually-hidden">Assistente IA: </span>
                              <AgentPresence
                                state="thinking"
                                size="micro"
                                colors={appearanceConfig.strandColors}
                                reducedMotion={prefersReducedMotion}
                                paused={!documentVisible}
                              />
                              <span>Sto preparando la risposta…</span>
                            </div>
                          ) : null}

                          {message.type === 'text' ? (
                            <>
                              {message.role === 'bot' ? (
                                <AgentPresence
                                  state={message.isError ? 'error' : 'settled'}
                                  size="micro"
                                  colors={appearanceConfig.strandColors}
                                  reducedMotion={prefersReducedMotion}
                                  paused={!documentVisible || messageIndex < messages.length - 1 || presenceState !== 'settled'}
                                />
                              ) : null}
                              <article
                                className="chatbot-message__content"
                                aria-labelledby={authorId}
                              >
                              {message.role !== 'user' && (<div className="chatbot-message__author-row">
                                <span id={authorId} className="chatbot-message__author">
                                  Assistente IA
                                </span>
                              </div>)}


                              {message.role === 'bot' ? (
                                (() => {
                                  const { cleanText, ctas } = parseCtas(message.text);

                                  return (
                                    <>
                                      <div
                                        className="chatbot-message__text"
                                        dangerouslySetInnerHTML={{
                                          __html: renderMarkdown(cleanText),
                                        }}
                                      />

                                      {message.isError && message.retryText ? (
                                        <BeamButton
                                          animated={effectsEnabled}
                                          type="button"
                                          className="chatbot-next-step chatbot-action-row"
                                          disabled={isSending}
                                          title="Ripristina la domanda nel campo di testo"
                                          onClick={() => handleCtaClick(message.retryText)}
                                        >
                                          <span>Riprendi domanda</span>
                                          <i
                                            className="bi bi-arrow-return-left"
                                            aria-hidden="true"
                                          />
                                        </BeamButton>
                                      ) : null}

                                      {ctas.length > 0 ? (
                                        <div
                                          className="chatbot-message__ctas"
                                          role="group"
                                          aria-labelledby={ctaLabelId}
                                        >
                                          <p
                                            id={ctaLabelId}
                                            className="chatbot-message__ctas-label"
                                          >
                                            Puoi continuare con
                                          </p>
                                          {ctas.map((cta, index) => (
                                            <BeamButton
                                              animated={effectsEnabled}
                                              key={`${cta}-${index}`}
                                              type="button"
                                              className="chatbot-next-step chatbot-action-row"
                                              disabled={isSending}
                                              title={cta}
                                              onClick={() => handleCtaClick(cta)}
                                            >
                                              <span>{cta}</span>
                                              <i
                                                className="bi bi-arrow-up-right"
                                                aria-hidden="true"
                                              />
                                            </BeamButton>
                                          ))}
                                        </div>
                                      ) : null}
                                    </>
                                  );
                                })()
                              ) : (
                                <p className="chatbot-message__text">{message.text}</p>
                              )}
                              </article>
                            </>
                          ) : null}
                        </li>
                      );
                    })}
                  </ol>
                ) : null}
              </m.main>
            )}
          </AnimatePresence>

          <footer className="chatbot-modal__footer">
            <ActionBeam
              className="chatbot-beam--composer"
              animated={effectsEnabled}
              busy={isSending}
              size="line"
            >
            <form
              ref={formRef}
              className="chatbot-input"
              data-state={composerState}
              onSubmit={handleExperienceSubmit}
            >
              <div className="chatbot-input__body">
                <span id={PROMPT_LABEL_ID} className="chatbot-input__label">
                  La tua domanda
                </span>

                <div
                  id={PROMPT_ID}
                  ref={promptRef}
                  className="chatbot-input__control"
                  role="textbox"
                  aria-labelledby={PROMPT_LABEL_ID}
                  aria-describedby={PROMPT_DESCRIPTION_ID}
                  aria-multiline="true"
                  aria-disabled={isSending}
                  contentEditable={!isSending}
                  suppressContentEditableWarning
                  tabIndex={isSending ? -1 : 0}
                  data-placeholder={labels.promptPlaceholder}
                  onBeforeInput={handlePromptBeforeInput}
                  onInput={handlePromptInput}
                  onFocus={() => setIsPromptFocused(true)}
                  onBlur={() => setIsPromptFocused(false)}
                  onPaste={handlePromptPaste}
                  onDrop={(event) => event.preventDefault()}
                  onKeyDown={handlePromptKeyDown}
                  onCompositionStart={handlePromptCompositionStart}
                  onCompositionEnd={handlePromptCompositionEnd}
                />
              </div>

              <div className="chatbot-input__actions">
                <div className="chatbot-input__tools">
                  {hasConversation ? (
                    <BeamButton
                      animated={effectsEnabled}
                      type="button"
                      className="chatbot-input__tool"
                      aria-label="Nuova conversazione"
                      title="Nuova conversazione"
                      onClick={resetChat}
                      disabled={isSending}
                    >
                      <i className="bi bi-arrow-repeat" aria-hidden="true" />
                    </BeamButton>
                  ) : null}

                  {contextCanBeSent ? (
                    <BeamButton
                      animated={effectsEnabled}
                      type="button"
                      className="chatbot-input__context"
                      aria-label={pageContextEnabled
                        ? 'Disattiva il contesto della pagina'
                        : 'Attiva il contesto della pagina'}
                      aria-pressed={pageContextEnabled}
                      title={pageContextEnabled
                        ? 'Contesto pagina attivo'
                        : 'Contesto pagina non usato'}
                      onClick={() => setPageContextEnabled((enabled) => !enabled)}
                      disabled={isSending}
                    >
                      <i className="bi bi-globe2" aria-hidden="true" />
                    </BeamButton>
                  ) : null}

                  <BeamButton
                    animated={effectsEnabled}
                    type="button"
                    className="chatbot-input__tool"
                    aria-label="Impostazioni e privacy"
                    aria-controls="chatbot-transparency-panel"
                    aria-expanded={isTransparencyOpen}
                    title="Impostazioni e privacy"
                    onClick={() => {
                      if (transparencyRef.current) {
                        transparencyRef.current.open = !transparencyRef.current.open;
                      }
                    }}
                    disabled={isSending}
                  >
                    <i className="bi bi-sliders" aria-hidden="true" />
                  </BeamButton>

                  <p id={PROMPT_DESCRIPTION_ID} className="chatbot-input__hint">
                    {labels.hint}
                  </p>
                </div>

                <BeamButton
                  animated={effectsEnabled}
                  type="submit"
                  className="chatbot-input__submit"
                  disabled={isSending || !draft.trim()}
                  aria-busy={isSending}
                  data-state={composerState}
                  aria-label={labels.submitTitle}
                  title={labels.submitTitle}
                >
                  <span className="chatbot-input__submit-label">
                    {isSending ? 'Invio…' : 'Invia'}
                  </span>
                  <span className="chatbot-input__submit-icon" aria-hidden="true">
                    {isSending ? '…' : composerState === 'success' ? '✓' : composerState === 'error' ? '!' : '↑'}
                  </span>
                </BeamButton>
              </div>
            </form>
            </ActionBeam>

            <div className="chatbot-footer__meta">
              <p
                className="chatbot-context-status"
                role="status"
                aria-live="polite"
              >
                <span aria-hidden="true" />
                {contextStatus}
              </p>

              <details
                ref={transparencyRef}
                className="chatbot-transparency"
                onToggle={(event) => setIsTransparencyOpen(event.currentTarget.open)}
              >
                <summary>Come funziona e privacy</summary>
                <div
                  id="chatbot-transparency-panel"
                  className="chatbot-transparency__content"
                >
                  {contextCanBeSent ? (
                    <label className="chatbot-checkbox chatbot-checkbox--context">
                      <input
                        type="checkbox"
                        checked={pageContextEnabled}
                        onChange={(event) => setPageContextEnabled(event.target.checked)}
                        disabled={isSending}
                      />
                      <span>Considera il contenuto pubblico di questa pagina</span>
                    </label>
                  ) : null}

                  <section aria-labelledby="chatbot-transparency-purpose">
                    <h3 id="chatbot-transparency-purpose">Sistema e scopo</h3>
                    <p>
                      Stai interagendo con un sistema di IA. Ti aiuta a comprendere
                      contenuti e fonti INAPP/PIAAC. Non valuta competenze, non
                      assegna livelli e non decide accesso a servizi, formazione o
                      benefici.
                    </p>
                  </section>

                  <section aria-labelledby="chatbot-transparency-data">
                    <h3 id="chatbot-transparency-data">Dati usati per rispondere</h3>
                    <p>
                      Sono inviati la domanda, la cronologia recente di questa
                      conversazione, il titolo e l'indirizzo della pagina senza
                      parametri. Il testo pubblico della pagina è incluso solo
                      quando l'indicatore segnala che il contesto è attivo.
                    </p>
                  </section>

                  <section aria-labelledby="chatbot-transparency-limits">
                    <h3 id="chatbot-transparency-limits">Limiti e dati da non inserire</h3>
                    <p>
                      Le risposte possono contenere errori: verifica le informazioni
                      importanti nelle fonti indicate. Non inserire credenziali,
                      dati sanitari, dati di terzi o altre informazioni riservate.
                    </p>
                  </section>

                  <section aria-labelledby="chatbot-transparency-rights">
                    <h3 id="chatbot-transparency-rights">Cancellazione e diritti</h3>
                    <p>
                      Nuova conversazione rimuove messaggi e bozza soltanto da
                      questa sessione del browser. Non elimina eventuali dati già inviati al
                      servizio. Destinatari, conservazione, contatto
                      umano e canale per i diritti devono essere indicati
                      nell'informativa del titolare prima della produzione.
                    </p>
                  </section>

                  {experienceConfig.persistenceEnabled ? (
                    <label className="chatbot-checkbox chatbot-checkbox--persistence">
                      <input
                        type="checkbox"
                        checked={persistIntro}
                        onChange={handlePersistenceChange}
                      />
                      <span>
                        Non mostrare più l’introduzione su questo dispositivo
                      </span>
                    </label>
                  ) : null}

                  <div className="chatbot-transparency__actions">
                    {experienceConfig.privacyUrl ? (
                      <a
                        href={experienceConfig.privacyUrl}
                        target="_blank"
                        rel="noreferrer"
                      >
                        Informativa e diritti
                      </a>
                    ) : (
                      <span className="chatbot-transparency__warning">
                        Informativa non configurata.
                      </span>
                    )}

                    {currentView !== EXPERIENCE_FIRST_RUN ? (
                      <BeamButton
                        animated={effectsEnabled}
                        type="button"
                        onClick={handleShowIntroAgain}
                      >
                        Mostra di nuovo l’introduzione
                      </BeamButton>
                    ) : null}
                  </div>
                </div>
              </details>
            </div>

            <p className="visually-hidden" aria-live="polite" aria-atomic="true">
              {liveAnnouncement}
            </p>
          </footer>
        </m.section>
      </m.div>
    </LazyMotion>
  );
};

export default ChatBot;
